import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Copy, ExternalLink, ArrowRight, Info } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/atoms/Button';
import { Tooltip } from '../../ui/tooltip';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '@/components/ui/molecules/InputGroup';

interface SuccessStateProps {
  jobId: string;
  onOpenJob: () => void;
}

const LINKEDIN_HELP_URL =
  'https://www.linkedin.com/help/linkedin/answer/a517570/applicant-options-for-your-job-post?lang=en';

export const SuccessState: React.FC<SuccessStateProps> = ({
  jobId,
  onOpenJob,
}) => {
  const { t } = useTranslation('dashboard');
  const shareUrl = `${window.location.origin}/job/${jobId}/cv`;
  const [copied, setCopied] = useState(false);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success(t('create_job_modal.link_copied'));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t('create_job_modal.copy_failed'));
    }
  };

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <p className="text-sm text-gray-600 font-body mb-5">
        {t('create_job_modal.success_desc')}
      </p>

      <div className="space-y-3 flex-1">
        <div className="flex items-center gap-1.5">
          <label className="block text-xs font-medium text-dark-invirtus font-heading uppercase tracking-wide">
            {t('create_job_modal.public_cv_link')}
          </label>
          <Tooltip
            content={t('create_job_modal.public_cv_tooltip')}
            variant="top"
          >
            <Info className="h-3.5 w-3.5 text-gray-400 hover:text-purple cursor-help" />
          </Tooltip>
        </div>

        <InputGroup size="md" className="w-full">
          <InputGroupInput
            readOnly
            value={shareUrl}
            rounded="md"
            endAddonPadding="icon"
            aria-label={t('create_job_modal.public_cv_link')}
            onClick={(e) => (e.target as HTMLInputElement).select()}
          />
          <InputGroupAddon align="inline-end">
            <InputGroupButton
              type="icon"
              intent="muted"
              icon={copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              onClick={copyLink}
              aria-label={copied ? t('create_job_modal.copied') : t('create_job_modal.copy_link')}
              title={copied ? t('create_job_modal.copied') : t('create_job_modal.copy_link')}
            />
          </InputGroupAddon>
        </InputGroup>

        <p className="text-xs text-gray-500 font-body pt-2">
          {t('create_job_modal.linkedin_hint')}{' '}
          <a
            href={LINKEDIN_HELP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-dark-invirtus font-medium hover:text-purple"
          >
            {t('create_job_modal.learn_how')}
            <ExternalLink className="h-3 w-3" />
          </a>
        </p>
      </div>

      <div className="flex justify-end pt-5 mt-auto border-t border-gray-200">
        <Button
          label={t('create_job_modal.open_job')}
          onClick={onOpenJob}
          type="contained"
          intent="primary"
          rightIcon={<ArrowRight/>}
          className="w-full sm:w-auto sm:min-w-[160px]"
        />
      </div>
    </div>
  );
};
